import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle, CheckCircle, Brain, Target, Zap } from 'lucide-react';
import { useNuancedContentFilter } from '@/hooks/useNuancedContentFilter';

const NuancedFilterTester: React.FC = () => {
  const [content, setContent] = useState('');
  const [platform, setPlatform] = useState('twitter');
  const [author, setAuthor] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [result, setResult] = useState<any>(null);
  
  const { analyzeContent, loading, error } = useNuancedContentFilter();

  const handleAnalyze = async () => {
    if (!content.trim()) return;

    const hashtagArray = hashtags ? hashtags.split(',').map(tag => tag.trim()) : [];
    
    const analysisResult = await analyzeContent({
      content,
      platform,
      author: author || undefined,
      hashtags: hashtagArray.length > 0 ? hashtagArray : undefined,
      context: {
        time_of_day: new Date().toISOString(),
        user_engagement_history: { average_engagement: 0.5 },
        content_frequency: 1
      }
    });
    
    setResult(analysisResult);
  };

  const getIntentBadgeColor = (intent: string) => {
    switch (intent) {
      case 'manipulative': return 'destructive';
      case 'promotional': return 'secondary';
      case 'spam': return 'destructive';
      case 'conversational': return 'default';
      case 'informational': return 'default';
      case 'entertainment': return 'default';
      default: return 'secondary';
    }
  };

  const sampleTexts = [
    "Just saw this amazing sunset! Perfect end to a great day 🌅",
    "URGENT!!! Limited time offer - Buy now and save 80%! Link in bio!",
    "What are your thoughts on the latest policy changes? Interested in hearing different perspectives.",
    "This is the WORST service ever! Completely useless idiots running this company!",
    "Like this post if you agree! Share for more content! Follow for daily updates!",
    "Breaking: Important news update about recent developments in technology sector",
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Nuanced Content Filter Tester
          </CardTitle>
          <CardDescription>
            Test the advanced filtering system with context-aware rule weighting, multi-factor decisions, and intent analysis
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="content">Content to Analyze</Label>
              <Textarea
                id="content"
                placeholder="Enter social media content to analyze..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={4}
              />
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="platform">Platform</Label>
                <select
                  id="platform"
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="twitter">Twitter/X</option>
                  <option value="reddit">Reddit</option>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="author">Author (optional)</Label>
                <Input
                  id="author"
                  placeholder="@username"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="hashtags">Hashtags (optional)</Label>
                <Input
                  id="hashtags"
                  placeholder="#tag1, #tag2, #tag3"
                  value={hashtags}
                  onChange={(e) => setHashtags(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Label className="text-sm font-medium">Quick test samples:</Label>
            {sampleTexts.map((sample, index) => (
              <Button
                key={index}
                variant="outline"
                size="sm"
                onClick={() => setContent(sample)}
                className="text-xs"
              >
                Sample {index + 1}
              </Button>
            ))}
          </div>

          <Button 
            onClick={handleAnalyze} 
            disabled={loading || !content.trim()}
            className="w-full"
          >
            {loading ? 'Analyzing...' : 'Analyze with Nuanced Filter'}
          </Button>

          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-destructive text-sm">{error}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Analysis Results
              </span>
              <Badge variant={result.shouldFilter ? 'destructive' : 'default'}>
                {result.shouldFilter ? 'FILTERED' : 'ALLOWED'}
              </Badge>
            </CardTitle>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label className="text-sm">Confidence Score</Label>
                <Progress value={result.confidence * 100} className="mt-1" />
                <span className="text-xs text-muted-foreground">
                  {Math.round(result.confidence * 100)}%
                </span>
              </div>
              <div className="flex-1">
                <Label className="text-sm">Weighted Score</Label>
                <Progress value={result.weighted_score * 100} className="mt-1" />
                <span className="text-xs text-muted-foreground">
                  {Math.round(result.weighted_score * 100)}%
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="intent" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="intent">Intent Analysis</TabsTrigger>
                <TabsTrigger value="rules">Rule Contributions</TabsTrigger>
                <TabsTrigger value="context">Context Factors</TabsTrigger>
                <TabsTrigger value="ai">AI Insights</TabsTrigger>
              </TabsList>
              
              <TabsContent value="intent" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Primary Intent</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <Badge variant={getIntentBadgeColor(result.intent_analysis.primary_intent)}>
                          {result.intent_analysis.primary_intent}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {Math.round(result.intent_analysis.intent_confidence * 100)}% confidence
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Intent Scores</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm">Commercial:</span>
                        <span className="text-sm font-medium">
                          {Math.round(result.intent_analysis.commercial_score * 100)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Manipulation:</span>
                        <span className="text-sm font-medium">
                          {Math.round(result.intent_analysis.manipulation_score * 100)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Engagement Seeking:</span>
                        <span className="text-sm font-medium">
                          {Math.round(result.intent_analysis.engagement_intent * 100)}%
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              
              <TabsContent value="rules" className="space-y-4">
                {result.rule_contributions.length > 0 ? (
                  <div className="space-y-3">
                    {result.rule_contributions.map((rule: any, index: number) => (
                      <Card key={index}>
                        <CardContent className="pt-4">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium">{rule.rule_name}</h4>
                            <Badge variant="outline">
                              Score: {Math.round(rule.weighted_score * 100)}%
                            </Badge>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Base Score:</span>
                              <div>{Math.round(rule.base_score * 100)}%</div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Context Modifier:</span>
                              <div>{rule.context_modifier.toFixed(1)}x</div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Final Score:</span>
                              <div className="font-medium">{Math.round(rule.weighted_score * 100)}%</div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    No rules were triggered by this content
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="context" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Time Relevance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {Math.round(result.context_factors.time_relevance * 100)}%
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Current time context weight
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Platform Fit</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {Math.round(result.context_factors.platform_appropriateness * 100)}%
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Platform appropriateness
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">User Pattern</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {Math.round(result.context_factors.user_behavior_pattern * 100)}%
                      </div>
                      <p className="text-sm text-muted-foreground">
                        User behavior context
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              
              <TabsContent value="ai" className="space-y-4">
                {result.ai_insights ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">Risk Scores</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {result.ai_insights.toxicityScore !== undefined && (
                          <div className="flex justify-between">
                            <span className="text-sm">Toxicity:</span>
                            <span className="text-sm font-medium">
                              {Math.round(result.ai_insights.toxicityScore * 100)}%
                            </span>
                          </div>
                        )}
                        {result.ai_insights.spamScore !== undefined && (
                          <div className="flex justify-between">
                            <span className="text-sm">Spam:</span>
                            <span className="text-sm font-medium">
                              {Math.round(result.ai_insights.spamScore * 100)}%
                            </span>
                          </div>
                        )}
                        {result.ai_insights.manipulationScore !== undefined && (
                          <div className="flex justify-between">
                            <span className="text-sm">Manipulation:</span>
                            <span className="text-sm font-medium">
                              {Math.round(result.ai_insights.manipulationScore * 100)}%
                            </span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">AI Detection</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-2 mb-2">
                          {result.ai_insights.shouldFilter ? (
                            <AlertCircle className="h-4 w-4 text-destructive" />
                          ) : (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          <span className="text-sm">
                            AI Recommendation: {result.ai_insights.shouldFilter ? 'Filter' : 'Allow'}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Confidence: {Math.round((result.ai_insights.confidence || 0) * 100)}%
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-8">
                    No AI insights available for this analysis
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {result.reasons && result.reasons.length > 0 && (
              <div className="mt-6">
                <Label className="text-sm font-medium">Filtering Reasons:</Label>
                <div className="mt-2 space-y-1">
                  {result.reasons.map((reason: string, index: number) => (
                    <div key={index} className="text-sm bg-muted p-2 rounded">
                      {reason}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default NuancedFilterTester;